import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Zap, Calendar, TrendingUp, Target, AlertCircle, CheckCircle, Play, Pause, Settings as SettingsIcon, Activity } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface AutomationRule {
  id: string;
  name: string;
  type: 'content_generation' | 'campaign_creation' | 'budget_optimization' | 'lead_nurture' | 'reporting';
  enabled: boolean;
  trigger: string;
  action: string;
  last_run: string | null;
  next_run: string | null;
  success_count: number;
  error_count: number;
}

interface AutomationMetric {
  label: string;
  value: string | number;
  change: number;
  trend: 'up' | 'down' | 'stable';
}

export default function MarketingAutomationEnginePage() {
  const navigate = useNavigate();
  const [automationRules, setAutomationRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'rules' | 'logs'>('overview');

  useEffect(() => {
    loadAutomationData();
  }, []);

  async function loadAutomationData() {
    setLoading(true);
    try {
      // Load automation rules from database
      // Currently no rules configured - showing empty state
      setAutomationRules([]);
    } catch (error) {
      console.error('Error loading automation data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function toggleRule(ruleId: string) {
    setAutomationRules(rules =>
      rules.map(rule =>
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      )
    );
    // In real implementation, would update database
  }

  const metrics: AutomationMetric[] = [
    { label: 'Active Automations', value: automationRules.filter(r => r.enabled).length, change: 0, trend: 'stable' },
    { label: 'Tasks Executed (24h)', value: 0, change: 0, trend: 'stable' },
    { label: 'Success Rate', value: '0%', change: 0, trend: 'stable' },
    { label: 'Time Saved (week)', value: '0 hours', change: 0, trend: 'stable' },
    { label: 'Content Generated', value: 0, change: 0, trend: 'stable' },
    { label: 'Leads Auto-Nurtured', value: 0, change: 0, trend: 'stable' }
  ];

  const getTypeIcon = (type: AutomationRule['type']) => {
    const icons = {
      content_generation: Bot,
      campaign_creation: Zap,
      budget_optimization: TrendingUp,
      lead_nurture: Target,
      reporting: Calendar
    };
    return icons[type];
  };

  const getTypeBadge = (type: AutomationRule['type']) => {
    const badges = {
      content_generation: { text: 'Content', color: 'bg-purple-100 text-purple-800' },
      campaign_creation: { text: 'Campaign', color: 'bg-blue-100 text-blue-800' },
      budget_optimization: { text: 'Budget', color: 'bg-green-100 text-green-800' },
      lead_nurture: { text: 'Lead Nurture', color: 'bg-orange-100 text-orange-800' },
      reporting: { text: 'Reporting', color: 'bg-gray-100 text-gray-800' }
    };
    const badge = badges[type];
    return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>{badge.text}</span>;
  };

  const formatNextRun = (nextRun: string | null) => {
    if (!nextRun) return 'Not scheduled';
    if (nextRun.includes('trigger')) return nextRun;
    
    const date = new Date(nextRun);
    const now = new Date();
    const diffHours = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'In <1 hour';
    if (diffHours < 24) return `In ${diffHours} hours`;
    return `In ${Math.floor(diffHours / 24)} days`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Bot className="text-blue-600" size={32} />
              <h1 className="text-3xl font-bold text-gray-900">Marketing Automation Engine</h1>
            </div>
            <p className="text-gray-600">
              Autonomous marketing system running 24/7 to optimize campaigns, generate content, and nurture leads
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-green-700">System Active</span>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
        {metrics.map((metric, idx) => (
          <div key={idx} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-xs font-medium">{metric.label}</span>
              {metric.trend === 'up' && <TrendingUp className="text-green-600" size={16} />}
              {metric.trend === 'down' && <TrendingUp className="text-red-600 transform rotate-180" size={16} />}
            </div>
            <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
            <div className={`text-xs mt-1 ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {metric.trend === 'up' ? '+' : ''}{metric.change}% vs last week
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(['overview', 'rules', 'logs'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center py-12">
              <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Automation Rules Configured</h3>
              <p className="text-gray-600 mb-6">
                The automation engine is ready to deploy. Configure your first automation rule to start optimizing your marketing.
              </p>
              <button 
                onClick={() => {
                  toast.success('Redirecting to Workflow Builder...');
                  setTimeout(() => navigate('/workflows/new'), 500);
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Zap size={20} />
                Create Automation Rule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Automation Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {automationRules.map(rule => {
                  const Icon = getTypeIcon(rule.type);
                  const successRate = rule.success_count + rule.error_count > 0
                    ? ((rule.success_count / (rule.success_count + rule.error_count)) * 100).toFixed(1)
                    : '0.0';

                  return (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleRule(rule.id)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            rule.enabled ? 'bg-green-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              rule.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Icon size={18} className="text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{rule.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getTypeBadge(rule.type)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-gray-600">{rule.trigger}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700">{formatNextRun(rule.next_run)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${
                            parseFloat(successRate) >= 95 ? 'text-green-600' : 
                            parseFloat(successRate) >= 90 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {successRate}%
                          </span>
                          <span className="text-xs text-gray-500">
                            ({rule.success_count}/{rule.success_count + rule.error_count})
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          Configure
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Execution Logs (Last 24 Hours)</h3>
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No automation execution logs yet</p>
            <p className="text-sm text-gray-500 mt-2">Logs will appear here once automation rules are active</p>
          </div>
        </div>
      )}

      {/* Documentation Section */}
      <div className="mt-8 space-y-6">
        {/* Quick Start Guide */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-blue-600 rounded-lg">
              <Bot className="text-white" size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Marketing Automation Engine</h2>
              <p className="text-gray-700">
                The automation engine runs 24/7 to execute marketing workflows, nurture leads, and optimize campaigns automatically.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Zap className="text-blue-600" size={20} />
                How It Works
              </h3>
              <ol className="text-sm text-gray-700 space-y-2">
                <li><strong>1. Create Rules:</strong> Define automation rules in the Workflow Builder</li>
                <li><strong>2. Set Triggers:</strong> Choose events that start the automation (e.g., "New Lead", "Membership Expires")</li>
                <li><strong>3. Add Conditions:</strong> Set criteria that must be met (e.g., "Lead Score &gt; 50")</li>
                <li><strong>4. Define Actions:</strong> Specify what happens (e.g., "Send Email", "Add to Segment")</li>
                <li><strong>5. Activate:</strong> Turn on the rule and the engine handles the rest automatically</li>
              </ol>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target className="text-green-600" size={20} />
                Getting Started
              </h3>
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  To create your first automation:
                </p>
                <button 
                  onClick={() => navigate('/workflows/new')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Zap size={18} />
                  Go to Workflow Builder
                </button>
                <p className="text-xs text-gray-600 mt-2">
                  Or use pre-built templates like "Win-Back Campaign" or "Lead Nurture Sequence"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Sources Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Activity className="text-purple-600" size={24} />
            Connected Data Sources
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-3">Database Tables</h4>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">automation_rules</code> - Rule definitions</li>
                <li>• <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">automation_executions</code> - Execution logs</li>
                <li>• <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">marketing_segment_customers</code> - Customer segments</li>
                <li>• <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">system_events</code> - Cross-module events</li>
                <li>• <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">contacts</code> - Customer data</li>
                <li>• <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">campaigns</code> - Marketing campaigns</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-3">Available Triggers</h4>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• <strong>Contact Created:</strong> New lead added</li>
                <li>• <strong>Membership Status:</strong> Subscription changes</li>
                <li>• <strong>Lead Score Change:</strong> Score threshold met</li>
                <li>• <strong>Campaign Activity:</strong> Email opened/clicked</li>
                <li>• <strong>Schedule:</strong> Time-based (daily, weekly)</li>
                <li>• <strong>Custom Event:</strong> Any system event</li>
              </ul>
            </div>

            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-3">Available Actions</h4>
              <ul className="text-sm text-gray-600 space-y-1.5">
                <li>• <strong>Send Email:</strong> Using templates</li>
                <li>• <strong>Send SMS:</strong> Text messaging</li>
                <li>• <strong>Add to Segment:</strong> Group management</li>
                <li>• <strong>Update Field:</strong> Contact data</li>
                <li>• <strong>Add/Remove Tag:</strong> Organization</li>
                <li>• <strong>Trigger Webhook:</strong> External systems</li>
                <li>• <strong>Create Task:</strong> Manual follow-up</li>
                <li>• <strong>Wait/Delay:</strong> Timing control</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Automations */}
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Common Automation Examples</h3>
          
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 mb-2">🎯 New Lead Welcome Sequence</h4>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Trigger:</strong> Contact Created → <strong>Action:</strong> Send welcome email → Wait 3 days → Send service overview email
              </p>
              <p className="text-xs text-gray-600">
                Automatically nurtures new leads with educational content
              </p>
            </div>

            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 mb-2">💚 High-Value Lead Alert</h4>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Trigger:</strong> Lead Score &gt; 75 → <strong>Condition:</strong> Not contacted in 7 days → <strong>Action:</strong> Create task for sales team
              </p>
              <p className="text-xs text-gray-600">
                Ensures hot leads get immediate attention
              </p>
            </div>

            <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 mb-2">🔄 Win-Back Campaign</h4>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Trigger:</strong> Membership Expires in 30 days → <strong>Action:</strong> Send renewal reminder → Add to "At Risk" segment
              </p>
              <p className="text-xs text-gray-600">
                Re-engages customers before they churn
              </p>
            </div>

            <div className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 mb-2">📊 Monthly Engagement Cleanup</h4>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Trigger:</strong> Schedule (1st of month) → <strong>Condition:</strong> No activity in 90 days → <strong>Action:</strong> Remove from active segments
              </p>
              <p className="text-xs text-gray-600">
                Keeps your segments clean and engagement rates high
              </p>
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Technical Details & Integration</h3>
          
          <div className="prose prose-sm max-w-none text-gray-700">
            <p className="mb-4">
              The automation engine connects to multiple database tables including <code>automation_rules</code>, 
              <code>automation_executions</code>, <code>marketing_segment_customers</code>, <code>system_events</code>, <code>contacts</code>, 
              and <code>campaigns</code>. It processes triggers in real-time, evaluates conditions using AND/OR logic, 
              executes actions sequentially, and logs all results for auditing.
            </p>
            
            <p className="mb-4">
              The system integrates with Email Templates, SMS Templates, Customer Segments, Lead Scoring, Workflow Builder, 
              Membership System, and Campaign Manager. All actions publish events to the <code>system_events</code> table 
              for cross-module communication.
            </p>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm font-medium text-blue-900">
                💡 <strong>Getting Started:</strong> Create your first automation rule in the Workflow Builder. 
                Start simple with a welcome email sequence, then build more complex multi-step workflows as you learn the system.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
